import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Clock, ArrowLeft, Calendar, User, ArrowUpRight, MapPin, Loader2 } from "lucide-react";
import axios from "axios";
import CTASection from "../components/CTASection";
import SEO from "../components/SEO";
import JsonLd from "../components/JsonLd";
import { breadcrumbSchema } from "../data/schema";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogArticle() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/blog/articles/${slug}`);
        setArticle(res.data);
        const all = await axios.get(`${API}/blog/articles`);
        setRelated((all.data.articles || []).filter((a) => a.slug !== slug).slice(0, 3));
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.error(e);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    })();
     
  }, [slug]);

  if (loading) {
    return (
      <div className="container-tight py-24 flex flex-col items-center gap-3 text-[#8a8580]">
        <Loader2 className="animate-spin text-[#d97757]" size={28} />
        <p>Chargement de l'article…</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container-tight py-24 text-center">
        <p className="text-[#8a8580]">Article introuvable.</p>
        <button onClick={() => navigate("/blog")} className="btn-outline mt-6">Retour au journal</button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={article.meta_title || article.title}
        description={article.meta_description || article.excerpt}
        keywords={article.keywords || []}
        image={article.cover}
        type="article"
      />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Accueil", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: article.title, path: `/blog/${article.slug}` },
        ])}
      />

      <article className="container-tight py-12 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#4a4642] hover:text-[#d97757] mb-8">
          <ArrowLeft size={16} /> Retour au journal
        </Link>

        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <span className="text-xs px-3 py-1 rounded-full bg-[#f0e4d5] text-[#b85e42] font-medium">{article.category}</span>
          {article.location && (
            <span className="text-xs px-3 py-1 rounded-full bg-[#f7f2ec] text-[#4a4642] font-medium flex items-center gap-1"><MapPin size={11} /> {article.location}</span>
          )}
          <span className="text-xs text-[#8a8580] flex items-center gap-1"><Clock size={12} /> {article.read_time} de lecture</span>
        </div>

        <h1 className="font-serif text-4xl md:text-5xl font-medium text-[#1a1815] leading-[1.1] tracking-tight">
          {article.title}
        </h1>

        <div className="mt-6 flex items-center gap-6 text-sm text-[#4a4642] flex-wrap">
          <span className="flex items-center gap-2"><User size={14} /> {article.author}</span>
          <span className="flex items-center gap-2"><Calendar size={14} /> {article.date}</span>
        </div>
      </article>

      <div className="container-tight max-w-4xl mb-12">
        <div className="rounded-[2rem] overflow-hidden aspect-[16/9]">
          <img src={article.cover} alt={article.title} className="w-full h-full object-cover" />
        </div>
      </div>

      <article className="container-tight max-w-3xl pb-16">
        <div>
          {(article.content || []).map((block, i) => {
            const key = `${block.type}-${i}`;
            if (block.type === "h2") return <h2 key={key} className="font-serif text-2xl md:text-3xl font-medium text-[#1a1815] mt-10 mb-4">{block.text}</h2>;
            if (block.type === "h3") return <h3 key={key} className="font-serif text-xl font-medium text-[#1a1815] mt-6 mb-3">{block.text}</h3>;
            return <p key={key} className="text-[#4a4642] text-[17px] leading-[1.8] mb-5">{block.text}</p>;
          })}
        </div>

        <div className="mt-14 p-8 rounded-3xl bg-[#f0e4d5]/50 border border-[#e8e0d5]">
          <p className="font-serif text-xl text-[#1a1815] leading-relaxed">
            Vous souhaitez être accompagné personnellement ? Le premier échange est offert.
          </p>
          <a
            href="https://www.resalib.fr/praticien/120570-jean-christophe-happillon-fasciatherapeute-quint-fonsegrives"
            target="_blank"
            rel="noreferrer"
            className="btn-primary mt-5"
          >
            Réserver mon bilan
          </a>
        </div>
      </article>

      {related.length > 0 && (
        <section className="container-tight pb-16">
          <h3 className="font-serif text-3xl font-medium text-[#1a1815] mb-8">À lire aussi</h3>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} className="group bg-white rounded-3xl border border-[#e8e0d5] overflow-hidden hover:border-[#d97757]/40 hover:shadow-lg transition-all">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={a.cover} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[#f7f2ec] text-[#4a4642] font-medium">{a.category}</span>
                  <h4 className="mt-3 font-serif text-lg font-medium text-[#1a1815] leading-snug group-hover:text-[#d97757] transition-colors">{a.title}</h4>
                  <div className="mt-3 flex items-center gap-1 text-sm font-medium text-[#d97757]">Lire <ArrowUpRight size={14} /></div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <CTASection />
    </>
  );
}
